import React, { useId, useRef, useState } from "react";
import { roleEditorText } from "./roleEditorMessages";

const COPY = {
  en: {
    search: "Find a permission or section", placeholder: "Search across all modules…",
    clear: "Clear", clearSearch: "Clear permission search", modules: "Permission modules", sections: "Permission sections",
    enabled: "Enabled / available permissions", purchasing: "Purchasing",
    count: (checked, total) => `${checked} of ${total} permissions enabled`,
    matches: (count) => `${count} matching ${count === 1 ? "section" : "sections"}`,
    summary: (sections, modules) => `${sections} matching ${sections === 1 ? "section" : "sections"} in ${modules} ${modules === 1 ? "module" : "modules"}. The tabs show matching sections. Select a section to review all its permissions.`,
    noMatches: "No matching sections. Try another permission name or clear the search.",
    noModuleMatches: "No matching sections in this module. Choose a module with matches or change the search.",
    empty: "No permissions available to manage. You can only assign permissions that you already have.",
  },
  es: {
    search: "Buscar un permiso o sección", placeholder: "Buscar en todos los módulos…",
    clear: "Limpiar", clearSearch: "Limpiar búsqueda de permisos", modules: "Módulos de permisos", sections: "Secciones de permisos",
    enabled: "Permisos activados / disponibles", purchasing: "Compras",
    count: (checked, total) => `${checked} de ${total} permisos activados`,
    matches: (count) => `${count} ${count === 1 ? "sección coincidente" : "secciones coincidentes"}`,
    summary: (sections, modules) => `${sections} ${sections === 1 ? "sección coincidente" : "secciones coincidentes"} en ${modules} ${modules === 1 ? "módulo" : "módulos"}. Las pestañas muestran las secciones coincidentes. Selecciona una sección para revisar todos sus permisos.`,
    noMatches: "No hay secciones coincidentes. Prueba otro nombre de permiso o limpia la búsqueda.",
    noModuleMatches: "No hay secciones coincidentes en este módulo. Elige un módulo con coincidencias o cambia la búsqueda.",
    empty: "No hay permisos disponibles para gestionar. Solo puedes asignar permisos que ya tienes.",
  },
  ca: {
    search: "Cerca un permís o secció", placeholder: "Cerca a tots els mòduls…",
    clear: "Neteja", clearSearch: "Neteja la cerca de permisos", modules: "Mòduls de permisos", sections: "Seccions de permisos",
    enabled: "Permisos activats / disponibles", purchasing: "Compres",
    count: (checked, total) => `${checked} de ${total} permisos activats`,
    matches: (count) => `${count} ${count === 1 ? "secció coincident" : "seccions coincidents"}`,
    summary: (sections, modules) => `${sections} ${sections === 1 ? "secció coincident" : "seccions coincidents"} en ${modules} ${modules === 1 ? "mòdul" : "mòduls"}. Les pestanyes mostren les seccions coincidents. Selecciona una secció per revisar tots els seus permisos.`,
    noMatches: "No hi ha seccions coincidents. Prova un altre nom de permís o neteja la cerca.",
    noModuleMatches: "No hi ha seccions coincidents en aquest mòdul. Tria un mòdul amb coincidències o canvia la cerca.",
    empty: "No hi ha permisos disponibles per gestionar. Només pots assignar permisos que ja tens.",
  },
};

const MODULE_ORDER = ["Global", "PMS", "CRM", "Purchasing"];
const normalizeSearch = (value) => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .trim();
const idPart = (value) => normalizeSearch(value).replace(/[^a-z0-9]+/g, "-");
const searchTerms = (value) => normalizeSearch(value).split(/\s+/).filter(Boolean);
const matchesTerms = (group, terms) => terms.every((term) => group.searchText.includes(term));

function getPermissionGroups(children) {
  return React.Children.toArray(children).flatMap((child) => {
    if (!React.isValidElement(child)) return [];
    if (child.type === React.Fragment) return getPermissionGroups(child.props.children);
    return child.props["data-permission-section"] && child.props["data-permission-module"]
      ? [child]
      : [];
  });
}

function describePermissions(children) {
  const result = { checked: 0, total: 0, text: [] };
  React.Children.forEach(children, (child) => {
    if (typeof child === "string" || typeof child === "number") {
      result.text.push(String(child));
    } else if (React.isValidElement(child)) {
      if (child.type === "input" && child.props.type === "checkbox") {
        result.total += 1;
        result.checked += child.props.checked ? 1 : 0;
      }
      const nested = describePermissions(child.props.children);
      result.checked += nested.checked;
      result.total += nested.total;
      result.text.push(...nested.text);
    }
  });
  return result;
}

/** Navigation only: permission controls and their authorization rules stay with the caller. */
export default function UsersRolesPermissionPanels({ children, languageId = "en" }) {
  const instanceId = useId().replace(/:/g, "");
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedSections, setSelectedSections] = useState({});
  const [query, setQuery] = useState("");
  const contentRefs = useRef({});
  const tabRefs = useRef({});
  const copy = COPY[languageId] || COPY.en;
  const duplicateIds = new Map();
  const groups = getPermissionGroups(children).map((element) => {
    const section = element.props["data-permission-section"];
    const module = element.props["data-permission-module"];
    const sectionLabel = roleEditorText(languageId, section);
    const moduleLabel = module === "Purchasing" ? copy.purchasing : module;
    const details = describePermissions(element);
    const baseId = `ur-permissions-${instanceId}-${idPart(module)}-${idPart(section)}`;
    const duplicate = duplicateIds.get(baseId) || 0;
    duplicateIds.set(baseId, duplicate + 1);
    const id = `${baseId}-${duplicate}`;
    return {
      element, section: sectionLabel, module, moduleLabel, id,
      checked: details.checked,
      total: details.total,
      searchText: normalizeSearch([moduleLabel, sectionLabel, ...details.text].join(" ")),
    };
  }).filter((group) => group.total > 0);
  const moduleNames = [...new Set(groups.map((group) => group.module))];
  const modules = [...MODULE_ORDER.filter((module) => moduleNames.includes(module)),
    ...moduleNames.filter((module) => !MODULE_ORDER.includes(module))].map((module) => ({
    name: module,
    label: module === "Purchasing" ? copy.purchasing : module,
    id: `ur-permissions-${instanceId}-module-${idPart(module)}`,
    groups: groups.filter((group) => group.module === module),
  }));
  const terms = searchTerms(query);
  const searching = terms.length > 0;
  const matches = groups.filter((group) => matchesTerms(group, terms));
  const matchingIds = new Set(matches.map((group) => group.id));
  const matchingModules = new Set(matches.map((group) => group.module));
  const activeModule = moduleNames.includes(selectedModule) ? selectedModule : modules[0]?.name;
  const searchId = `ur-permissions-search-${instanceId}`;

  const updateQuery = (value) => {
    const nextMatches = groups.filter((group) => matchesTerms(group, searchTerms(value)));
    if (nextMatches.length && !nextMatches.some((group) => group.module === activeModule)) {
      setSelectedModule(nextMatches[0].module);
    }
    setQuery(value);
  };

  const selectSection = (module, id) => {
    setSelectedSections((previous) => ({ ...previous, [module]: id }));
    const content = contentRefs.current[module];
    const modalBody = content?.closest(".ur-modalBody");
    if (!modalBody) return;
    const top = Math.max(0, modalBody.scrollTop + content.getBoundingClientRect().top
      - modalBody.getBoundingClientRect().top - modalBody.clientTop - 12);
    if (typeof modalBody.scrollTo === "function") {
      modalBody.scrollTo({ top, behavior: "auto" });
    } else {
      modalBody.scrollTop = top;
    }
  };

  const handleTabKeyDown = (event, index) => {
    const nextIndex = {
      ArrowRight: (index + 1) % modules.length,
      ArrowLeft: (index - 1 + modules.length) % modules.length,
      Home: 0,
      End: modules.length - 1,
    }[event.key];
    if (nextIndex === undefined) return;
    event.preventDefault();
    const nextModule = modules[nextIndex].name;
    setSelectedModule(nextModule);
    tabRefs.current[nextModule]?.focus();
  };

  return (
    <div className="ur-permissionsWorkspace">
      <div className="ur-permissionSearch">
        <label htmlFor={searchId}>{copy.search}</label>
        <div className="ur-permissionSearchControl">
          <input id={searchId} type="search" value={query} placeholder={copy.placeholder} onChange={(event) => updateQuery(event.target.value)} />
          {query && (
            <button className="ur-permissionSearchClear" type="button" aria-label={copy.clearSearch} onClick={() => updateQuery("")}>
              {copy.clear}
            </button>
          )}
        </div>
      </div>
      {modules.length > 0 && (
        <div className="ur-permissionModuleTabs" role="tablist" aria-label={copy.modules}>
          {modules.map((module, index) => {
            const total = module.groups.reduce((sum, group) => sum + group.total, 0);
            const checked = module.groups.reduce((sum, group) => sum + group.checked, 0);
            const matchCount = module.groups.filter((group) => matchingIds.has(group.id)).length;
            return (
              <button
                key={module.name}
                id={`${module.id}-tab`}
                ref={(element) => { tabRefs.current[module.name] = element; }}
                type="button"
                role="tab"
                aria-selected={module.name === activeModule}
                aria-controls={module.id}
                tabIndex={module.name === activeModule ? 0 : -1}
                onClick={() => setSelectedModule(module.name)}
                onKeyDown={(event) => handleTabKeyDown(event, index)}
              >
                <span>{module.label}</span>
                <span className="ur-permissionCount" aria-label={searching ? copy.matches(matchCount) : copy.count(checked, total)}>
                  {searching ? matchCount : `${checked} / ${total}`}
                </span>
              </button>
            );
          })}
        </div>
      )}
      {searching && (
        <p className="ur-permissionSearchSummary" role="status">
          {matches.length ? copy.summary(matches.length, matchingModules.size) : copy.noMatches}
        </p>
      )}
      {!groups.length && !searching && <p className="ur-permissionEmpty" role="status">{copy.empty}</p>}
      {modules.map((module) => {
        const moduleMatches = module.groups.filter((group) => matchingIds.has(group.id));
        const activeId = moduleMatches.some((group) => group.id === selectedSections[module.name])
          ? selectedSections[module.name] : moduleMatches[0]?.id;
        return (
          <div
            key={module.name}
            id={module.id}
            className="ur-permissionsLayout"
            role="tabpanel"
            aria-labelledby={`${module.id}-tab`}
            tabIndex={0}
            hidden={module.name !== activeModule}
          >
            <div className="ur-permissionSidebar">
              <p className="ur-permissionNavHint">{copy.enabled}</p>
              <nav className="ur-permissionNav" aria-label={`${module.label}: ${copy.sections}`}>
                {module.groups.map((group) => (
                  <button
                    key={group.id}
                    id={`${group.id}-navigation`}
                    type="button"
                    hidden={!matchingIds.has(group.id)}
                    aria-current={group.id === activeId ? "true" : undefined}
                    aria-controls={group.id}
                    onClick={() => selectSection(module.name, group.id)}
                  >
                    <span className="ur-permissionNavText"><strong>{group.section}</strong></span>
                    <span className="ur-permissionCount" aria-label={copy.count(group.checked, group.total)}>{group.checked} / {group.total}</span>
                  </button>
                ))}
              </nav>
            </div>
            <div className="ur-permissionContent" ref={(element) => { contentRefs.current[module.name] = element; }}>
              {searching && matches.length > 0 && moduleMatches.length === 0 && <p className="ur-permissionEmpty" role="status">{copy.noModuleMatches}</p>}
              {module.groups.map((group) => (
                <section
                  key={group.id}
                  id={group.id}
                  className="ur-permissionSection"
                  aria-labelledby={`${group.id}-navigation`}
                  hidden={group.id !== activeId}
                >
                  {group.element}
                </section>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
